import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useTeams } from '@/hooks/useTeams'
import { useTDDEligibility } from '@/hooks/useStandings'
import { getApiSeasonYear, TDD_ELIGIBILITY_SEASON } from '@/lib/season'
import { useSeason } from '@/context/SeasonContext'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { getInformalName, getTeamLogo } from '@/lib/teams'
import { LeagueRules } from './components'
import type {
  ScoringRule,
  ScoringExample,
  TeamEntry,
  TeamSelectionRules,
  TiebreakerRules,
} from './types'

// =============================================================================
// Helpers
// =============================================================================

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

function buildScoringRules(sr: import('@/hooks/useLeagueConfig').ScoringRules): ScoringRule[] {
  return [
    {
      id: 'win',
      label: 'Win',
      points: sr.win,
      description: 'Your picked team wins their match.',
      color: 'green',
    },
    {
      id: 'draw',
      label: 'Draw',
      points: sr.draw,
      description: 'Your picked team draws their match.',
      color: 'neutral',
    },
    {
      id: 'loss',
      label: 'Loss',
      points: sr.loss,
      description: 'Your picked team loses their match.',
      color: 'neutral',
    },
    {
      id: 'red-card',
      label: 'Red Card',
      points: sr.red_card,
      description: 'Your picked team receives a red card during the match. Applied on top of the base result.',
      color: 'red',
    },
    {
      id: 'gd-bonus',
      label: 'Goal Difference Bonus',
      points: sr.win_by_3_plus_bonus,
      description: 'If your picked team wins by 3+ goals, you earn +1 bonus point for every 3 goals of winning margin. A 3-0 win = +1 GD bonus. A 6-0 win = +2 GD bonus.',
      color: 'green',
    },
    {
      id: 'tdd',
      label: 'Double Down (DD)',
      points: null,
      description: 'Doubles the base points (win/draw/loss) plus any GD bonus. Also doubles the red card penalty if one occurs. Only usable on DD-eligible teams.',
      color: 'violet',
    },
  ]
}

function buildScoringExamples(sr: import('@/hooks/useLeagueConfig').ScoringRules): ScoringExample[] {
  return [
    {
      id: 'standard-win',
      title: 'Standard Win',
      team: 'Team A',
      opponent: 'Team B',
      score: '3-1',
      isHome: true,
      breakdown: [
        { label: 'Win', points: sr.win },
        { label: 'GD Bonus (3-1 = +2 margin, 0 bonus)', points: 0 },
      ],
      total: sr.win,
      note: 'Goal difference must exceed 2 to earn a bonus.',
    },
    {
      id: 'win-with-gd',
      title: 'Win with GD Bonus',
      team: 'Team A',
      opponent: 'Team C',
      score: '4-0',
      isHome: true,
      breakdown: [
        { label: 'Win', points: sr.win },
        { label: 'GD Bonus (4-0 = +4 margin, +1 per 3 GD)', points: sr.win_by_3_plus_bonus },
      ],
      total: sr.win + sr.win_by_3_plus_bonus,
      note: 'Every 3 goals of winning margin earns +1 bonus. A 6-0 win = +2 bonus.',
    },
    {
      id: 'tdd-win',
      title: 'DD Win',
      team: 'Team D',
      opponent: 'Team E',
      score: '2-0',
      isHome: false,
      tddUsed: true,
      breakdown: [
        { label: 'Win', points: sr.win },
        { label: 'DD Doubles', points: sr.win },
      ],
      total: sr.win * 2,
      note: `DD doubles the base result. Win (${sr.win}) x 2 = ${sr.win * 2} points.`,
    },
    {
      id: 'win-with-red-card',
      title: 'Win with Red Card',
      team: 'Team F',
      opponent: 'Team G',
      score: '1-0',
      isHome: true,
      breakdown: [
        { label: 'Win', points: sr.win },
        { label: 'Red Card', points: sr.red_card },
      ],
      total: sr.win + sr.red_card,
      note: `You still earn points for winning, but the red card penalty brings the net to just ${sr.win + sr.red_card > 0 ? '+' : ''}${sr.win + sr.red_card}.`,
    },
    {
      id: 'loss-with-red-card',
      title: 'Loss with Red Card',
      team: 'Team H',
      opponent: 'Team I',
      score: '0-2',
      isHome: true,
      breakdown: [
        { label: 'Loss', points: sr.loss },
        { label: 'Red Card', points: sr.red_card },
      ],
      total: sr.loss + sr.red_card,
      note: 'A loss earns 0 points, and the red card penalty puts you in the negative.',
    },
  ]
}

function buildTeamSelectionRules(teamPickLimit: number): TeamSelectionRules {
  return {
    picksPerMatchday: 1,
    maxUsesPerTeamPerSeason: teamPickLimit,
    autopickOnMissedDeadline: true,
    autopickMethod: 'If the selection deadline is missed, an automatic pick will be made based on the team with the lowest betting odds.',
    rules: [
      'Each manager selects one team to win per matchday.',
      `Each team can only be picked ${teamPickLimit} time${teamPickLimit !== 1 ? 's' : ''} per season (across both halves).`,
      'If you miss the deadline, an automatic pick will be made based on the team with the lowest betting odds.',
      'Autopick selections count toward your team usage limit.',
    ],
  }
}

const TIEBREAKER_RULES: TiebreakerRules = {
  order: [
    { priority: 1, label: 'Total Points', description: 'Manager with the most points wins.' },
    { priority: 2, label: 'Total Wins', description: 'If tied on points, the manager with more wins ranks higher.' },
    { priority: 3, label: 'Fewest Losses', description: 'If still tied, the manager with fewer losses ranks higher.' },
    { priority: 4, label: 'Head-to-Head Character Assessment', description: 'If all else is equal, league commissioner makes a character-based judgment call.' },
  ],
}

// =============================================================================
// Section Component
// =============================================================================

export function LeagueRulesSection() {
  const { season } = useSeason()
  const apiYear = getApiSeasonYear(season)
  const { data: teamsData, isLoading: isLoadingTeams } = useTeams(apiYear)
  const { getTDDEligibleTeams, getTDDIneligibleTeams, isLoading: isLoadingTDD } = useTDDEligibility()
  const { config: leagueConfig, seasonSplits, scoringRules, prizeStructure, hasPromotion } = useLeagueConfig()
  const teams = teamsData?.teams

  // Build TDD eligible teams with logos
  const tddEligibleTeams: TeamEntry[] = useMemo(() => {
    const eligibleTeams = getTDDEligibleTeams()

    return eligibleTeams.map((team) => {
      const shortName = getInformalName(team.name)

      return {
        id: shortName.toLowerCase().replace(/\s+/g, '-'),
        shortName,
        fullName: team.name,
        logo: getTeamLogo(team.name, teams),
        priorSeasonPosition: team.position ?? null,
        promoted: team.promoted ?? false,
      }
    }).sort((a, b) => {
      // Sort promoted teams last, then by position
      if (a.promoted && !b.promoted) return 1
      if (!a.promoted && b.promoted) return -1
      if (a.priorSeasonPosition === null) return 1
      if (b.priorSeasonPosition === null) return -1
      return a.priorSeasonPosition - b.priorSeasonPosition
    })
  }, [teams, getTDDEligibleTeams])

  // Build TDD ineligible teams with logos
  const tddIneligibleTeams: TeamEntry[] = useMemo(() => {
    const ineligibleTeams = getTDDIneligibleTeams()

    return ineligibleTeams.map((team) => {
      const shortName = getInformalName(team.name)

      return {
        id: shortName.toLowerCase().replace(/\s+/g, '-'),
        shortName,
        fullName: team.name,
        logo: getTeamLogo(team.name, teams),
        priorSeasonPosition: team.position ?? null,
      }
    }).sort((a, b) => {
      if (a.priorSeasonPosition === null) return 1
      if (b.priorSeasonPosition === null) return -1
      return a.priorSeasonPosition - b.priorSeasonPosition
    })
  }, [teams, getTDDIneligibleTeams])

  // Build season structure from league config instead of hardcoded template
  // (must be called before early returns to respect hook ordering rules)
  const dynamicSeasonStructure = useMemo(() => {
    const splits = seasonSplits
    const tddPerHalf = Math.floor(leagueConfig.tdd_limit / Math.max(splits.length, 1))

    return {
      season,
      halves: splits.map(s => ({
        name: s.name,
        matchdays: `${s.startMatchday}-${s.endMatchday}`,
        startMatchday: s.startMatchday,
        endMatchday: s.endMatchday,
        tddAllowance: tddPerHalf,
      })),
      totalMatchdays: leagueConfig.total_matchdays,
      totalTddPerSeason: leagueConfig.tdd_limit,
      tddCarryOver: false,
      description: `The season is split into ${splits.length} halves. Each manager gets ${tddPerHalf} DD uses per half (${leagueConfig.tdd_limit} total). Unused DD does not carry over between halves.`,
    }
  }, [season, seasonSplits, leagueConfig])

  if (isLoadingTeams || isLoadingTDD) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  // Build dynamic subtitles based on config
  const ineligibleCount = leagueConfig.tdd_ineligible_count
  const eligibilitySeason = TDD_ELIGIBILITY_SEASON
  const eligibilitySeasonLabel = `${eligibilitySeason}/${String(parseInt(eligibilitySeason) + 1).slice(-2)}`
  const promotedCount = tddEligibleTeams.filter(t => t.promoted).length

  const eligibleRangeStart = ordinal(ineligibleCount + 1)
  const tddEligibleSubtitle = hasPromotion && promotedCount > 0
    ? `${eligibleRangeStart} and below from ${eligibilitySeasonLabel} season plus ${promotedCount} promoted club${promotedCount !== 1 ? 's' : ''}. DD can only be used on these teams.`
    : `${eligibleRangeStart} and below from ${eligibilitySeasonLabel} season. DD can only be used on these teams.`

  const tddIneligibleSubtitle = `Top ${ineligibleCount} from ${eligibilitySeasonLabel} season. DD cannot be used on these teams.`

  return (
    <LeagueRules
      seasonStructure={dynamicSeasonStructure}
      scoringRules={buildScoringRules(scoringRules)}
      scoringExamples={buildScoringExamples(scoringRules)}
      tddEligibleTeams={tddEligibleTeams}
      tddEligibleSubtitle={tddEligibleSubtitle}
      tddIneligibleTeams={tddIneligibleTeams}
      tddIneligibleSubtitle={tddIneligibleSubtitle}
      teamSelectionRules={buildTeamSelectionRules(leagueConfig.team_pick_limit)}
      tiebreakerRules={TIEBREAKER_RULES}
      prizeStructure={prizeStructure ?? undefined}
    />
  )
}
